import axios from 'axios';

const PMS_API_URL = '/api/pms';

/** -----------------------------------------
 * 1. 지시 (Instructions) API
 * ----------------------------------------- */
export const getInstructions = async (params, token) => {
  // params should include: type ('given' or 'received'), plus any search parameters
  const response = await axios.get(`${PMS_API_URL}/instructions`, {
    params: params,
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const getInstructionById = async (id, token) => {
  const response = await axios.get(`${PMS_API_URL}/instructions/${id}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const createInstruction = async (data, token) => {
  const response = await axios.post(`${PMS_API_URL}/instructions`, data, {
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
  });
  return response.data;
};

export const updateInstruction = async (id, data, token) => {
  const response = await axios.put(`${PMS_API_URL}/instructions/${id}`, data, {
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
  });
  return response.data;
};

export const deleteInstruction = async (id, token) => {
  const response = await axios.delete(`${PMS_API_URL}/instructions/${id}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const cancelInstruction = async (id, token) => {
  const response = await axios.put(`${PMS_API_URL}/instructions/${id}/status`, { status: 'canceled' }, {
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
  });
  return response.data;
};

/** -----------------------------------------
 * 2. 프로젝트 (Projects) API
 * ----------------------------------------- */
export const getProjects = async (params, token) => {
  // params should include: status ('in_progress', 'review', 'completed', 'canceled'), plus search params
  const response = await axios.get(`${PMS_API_URL}/projects`, {
    params: params,
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const getProjectById = async (id, token) => {
  const response = await axios.get(`${PMS_API_URL}/projects/${id}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const createProject = async (formData, token) => {
  // formData must be an instance of FormData for multipart/form-data
  const response = await axios.post(`${PMS_API_URL}/projects`, formData, {
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

export const updateProject = async (id, formData, token) => {
  const response = await axios.put(`${PMS_API_URL}/projects/${id}`, formData, {
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

export const deleteProject = async (id, token) => {
  const response = await axios.delete(`${PMS_API_URL}/projects/${id}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const deleteProjectHard = async (id, token) => {
  const response = await axios.delete(`${PMS_API_URL}/projects/${id}/hard`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const updateProjectStatus = async (id, status, review_opinion, token) => {
  const response = await axios.put(`${PMS_API_URL}/projects/${id}/status`, { status, review_opinion }, {
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
  });
  return response.data;
};

/** -----------------------------------------
 * 3. 프로젝트 룸 (Categories & Tasks) API
 * ----------------------------------------- */
export const getCategories = async (projectId, token) => {
  const response = await axios.get(`${PMS_API_URL}/projects/${projectId}/categories`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const syncProjectWatchersToTasks = async (projectId, token) => {
  const response = await axios.post(`${PMS_API_URL}/projects/${projectId}/sync-watchers`, {}, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const createCategory = async (projectId, data, token) => {
  const response = await axios.post(`${PMS_API_URL}/projects/${projectId}/categories`, data, {
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
  });
  return response.data;
};

export const updateCategory = async (categoryId, data, token) => {
  const response = await axios.put(`${PMS_API_URL}/categories/${categoryId}`, data, {
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
  });
  return response.data;
};

export const deleteCategory = async (categoryId, token) => {
  const response = await axios.delete(`${PMS_API_URL}/categories/${categoryId}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const getTasks = async (projectId, token) => {
  const response = await axios.get(`${PMS_API_URL}/projects/${projectId}/tasks`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const createTask = async (projectId, data, token) => {
  const response = await axios.post(`${PMS_API_URL}/projects/${projectId}/tasks`, data, {
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
  });
  return response.data;
};

export const updateTask = async (taskId, data, token) => {
  const response = await axios.put(`${PMS_API_URL}/tasks/${taskId}`, data, {
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
  });
  return response.data;
};

export const deleteTask = async (taskId, token) => {
  const response = await axios.delete(`${PMS_API_URL}/tasks/${taskId}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const updateTaskStatus = async (taskId, status, token) => {
  const response = await axios.put(`${PMS_API_URL}/tasks/${taskId}/status`, { status }, {
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
  });
  return response.data;
};

export const updateTaskCategory = async (taskId, categoryId, token) => {
  const response = await axios.put(`${PMS_API_URL}/tasks/${taskId}/category`, { category_id: categoryId }, {
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
  });
  return response.data;
};

export const reorderTasks = async (categoryId, taskOrders, token) => {
  const response = await axios.put(`${PMS_API_URL}/categories/${categoryId}/tasks/reorder`, { taskOrders }, {
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
  });
  return response.data;
};

/** -----------------------------------------
 * 4. 타스크 드로어 패널 (Checklists, Files & Comments) API
 * ----------------------------------------- */
export const getTaskFiles = async (taskId, token) => {
  const response = await axios.get(`${PMS_API_URL}/tasks/${taskId}/files`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const uploadTaskFiles = async (taskId, formData, token) => {
  const response = await axios.post(`${PMS_API_URL}/tasks/${taskId}/files`, formData, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'multipart/form-data'
    }
  });
  return response.data;
};

export const createTaskFolder = async (taskId, folderName, token) => {
  const response = await axios.post(`${PMS_API_URL}/tasks/${taskId}/folders`, { folder_name: folderName }, {
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
  });
  return response.data;
};

export const renameTaskFolder = async (taskId, oldFolderName, newFolderName, token) => {
  const response = await axios.put(`${PMS_API_URL}/tasks/${taskId}/folders/rename`, { oldFolderName, newFolderName }, {
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
  });
  return response.data;
};

export const getChecklists = async (taskId, token) => {
  const response = await axios.get(`${PMS_API_URL}/tasks/${taskId}/checklists`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const createChecklist = async (taskId, data, token) => {
  const response = await axios.post(`${PMS_API_URL}/tasks/${taskId}/checklists`, data, {
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
  });
  return response.data;
};

export const updateChecklist = async (id, data, token) => {
  const response = await axios.put(`${PMS_API_URL}/checklists/${id}`, data, {
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
  });
  return response.data;
};

export const deleteChecklist = async (id, token) => {
  const response = await axios.delete(`${PMS_API_URL}/checklists/${id}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const getComments = async (taskId, token) => {
  const response = await axios.get(`${PMS_API_URL}/tasks/${taskId}/comments`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const createComment = async (taskId, data, token) => {
  const response = await axios.post(`${PMS_API_URL}/tasks/${taskId}/comments`, data, {
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
  });
  return response.data;
};

export const updateComment = async (id, data, token) => {
  const response = await axios.put(`${PMS_API_URL}/comments/${id}`, data, {
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
  });
  return response.data;
};

export const deleteComment = async (id, token) => {
  const response = await axios.delete(`${PMS_API_URL}/comments/${id}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const updateCommentFolder = async (commentId, folderName, token) => {
  const response = await axios.put(`${PMS_API_URL}/comments/${commentId}/folder`, { folder_name: folderName }, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

// ==========================================
// [5. 코멘트 (Task Comments & Project Instructions)]
// ==========================================
export const getTaskComments = async (taskId, token) => {
  const response = await axios.get(`${PMS_API_URL}/tasks/${taskId}/comments`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const getAllTaskCommentsByProject = async (projectId, token) => {
  const response = await axios.get(`${PMS_API_URL}/projects/${projectId}/all-task-comments`, {
    params: { _t: Date.now() },
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const createTaskComment = async (taskId, content, token) => {
  const response = await axios.post(`${PMS_API_URL}/tasks/${taskId}/comments`, { content }, {
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
  });
  return response.data;
};

export const getProjectInstructions = async (projectId, token) => {
  const response = await axios.get(`${PMS_API_URL}/projects/${projectId}/instructions`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const createProjectInstruction = async (projectId, content, token) => {
  const response = await axios.post(`${PMS_API_URL}/projects/${projectId}/instructions`, { content }, {
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
  });
  return response.data;
};

// ==========================================
// [6. 피드백 시스템 (Feedbacks)]
// ==========================================
export const getFeedbacks = async (projectId, token) => {
  const response = await axios.get(`${PMS_API_URL}/projects/${projectId}/feedbacks`, {
    params: { _t: Date.now() },
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const createFeedback = async (projectId, data, token) => {
  const response = await axios.post(`${PMS_API_URL}/projects/${projectId}/feedbacks`, data, {
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
  });
  return response.data;
};

export const updateFeedback = async (feedbackId, data, token) => {
  const response = await axios.put(`${PMS_API_URL}/feedbacks/${feedbackId}`, data, {
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
  });
  return response.data;
};

export const deleteFeedback = async (feedbackId, token) => {
  const response = await axios.delete(`${PMS_API_URL}/feedbacks/${feedbackId}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const toggleFeedbackResolve = async (feedbackId, token) => {
  const response = await axios.put(`${PMS_API_URL}/feedbacks/${feedbackId}/resolve`, {}, {
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
  });
  return response.data;
};

export const updateProjectVisit = async (projectId, token) => {
  const response = await axios.put(`${PMS_API_URL}/projects/${projectId}/visit`, {}, {
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
  });
  return response.data;
};

export const updateMentionOnly = async (projectId, mentionOnly, token) => {
  const response = await axios.put(`${PMS_API_URL}/projects/${projectId}/members/mention-only`, { mention_only: mentionOnly }, {
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
  });
  return response.data;
};

/** -----------------------------------------
 * 7. Business Order API
 * ----------------------------------------- */
export const getBusinessOrder = async (projectId, token) => {
  const response = await axios.get(`${PMS_API_URL}/projects/${projectId}/business-order`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const saveBusinessOrder = async (projectId, data, token) => {
  const response = await axios.post(`${PMS_API_URL}/projects/${projectId}/business-order`, data, {
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
  });
  return response.data;
};

export const deleteBusinessOrder = async (projectId, token) => {
  const response = await axios.delete(`${PMS_API_URL}/projects/${projectId}/business-order`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};
